import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Bell, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import { useArrearsList, useSendPaymentReminder } from '@/hooks/useFinanceAnalytics';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { pl } from 'date-fns/locale';

export default function ArrearsList({ branchId }) {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const { data: arrears, isLoading, error } = useArrearsList(branchId);
  const sendReminder = useSendPaymentReminder();

  // Toggle selection
  const toggleSelection = (receiptId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(receiptId)) {
        newSet.delete(receiptId);
      } else {
        newSet.add(receiptId);
      }
      return newSet;
    });
  };

  // Toggle all
  const toggleAll = () => {
    if (selectedIds.size === arrears?.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(arrears?.map(a => a.receipt_id)));
    }
  };

  // Get selected receipts
  const selectedReceipts = arrears?.filter(a => selectedIds.has(a.receipt_id)) || [];
  const totalSelectedAmount = selectedReceipts.reduce((sum, r) => sum + r.total_amount_cents, 0);

  // Handle send reminders
  const handleSendReminders = async () => {
    try {
      const result = await sendReminder.mutateAsync({
        receiptIds: Array.from(selectedIds),
        branchId
      });

      if (result.sent_count > 0) {
        toast.success(`Wysłano ${result.sent_count} przypomnień`);
      }
      if (result.failed_count > 0) {
        toast.error(`Nie udało się wysłać ${result.failed_count} przypomnień`);
      }

      setSelectedIds(new Set());
      setConfirmDialogOpen(false);
    } catch (error) {
      toast.error('Nie udało się wysłać przypomnień');
    }
  };

  // Navigate to receipt
  const handleRowClick = (receiptId) => {
    navigate(`/Billing?receipt=${receiptId}`);
  };

  // Format currency
  const formatCurrency = (cents) => {
    return (cents / 100).toLocaleString('pl-PL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Format last reminder time
  const formatReminderTime = (timestamp) => {
    if (!timestamp) return null;
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true, locale: pl });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-64 mt-1" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-slate-500">Nie udało się załadować listy zaległości</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="text-base">Zaległe rachunki</CardTitle>
            <CardDescription>
              {arrears?.length || 0} rachunków po terminie płatności
            </CardDescription>
          </div>
          {selectedIds.size > 0 && (
            <Button
              onClick={() => setConfirmDialogOpen(true)}
              className="gap-2"
              size="sm"
            >
              <Bell className="w-4 h-4" />
              Wyślij przypomnienie ({selectedIds.size})
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {!arrears || arrears.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-slate-500">
              <AlertCircle className="w-12 h-12 text-emerald-400 mb-2" />
              <p>Brak zaległych rachunków</p>
              <p className="text-sm text-slate-400">Wszyscy rodzice opłacili rachunki na czas</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="py-3 px-2 text-left">
                      <Checkbox
                        checked={selectedIds.size === arrears.length && arrears.length > 0}
                        onCheckedChange={toggleAll}
                      />
                    </th>
                    <th className="py-3 px-2 text-left text-sm font-medium text-slate-500">Dziecko</th>
                    <th className="py-3 px-2 text-right text-sm font-medium text-slate-500">Kwota</th>
                    <th className="py-3 px-2 text-right text-sm font-medium text-slate-500">Dni po terminie</th>
                    <th className="py-3 px-2 text-left text-sm font-medium text-slate-500">Status</th>
                    <th className="py-3 px-2 text-left text-sm font-medium text-slate-500">Przyp.</th>
                    <th className="py-3 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {arrears.map((item) => (
                    <tr
                      key={item.receipt_id}
                      className={cn(
                        "border-b border-slate-100 hover:bg-slate-50 transition-colors",
                        selectedIds.has(item.receipt_id) && "bg-blue-50"
                      )}
                    >
                      <td className="py-3 px-2">
                        <Checkbox
                          checked={selectedIds.has(item.receipt_id)}
                          onCheckedChange={() => toggleSelection(item.receipt_id)}
                        />
                      </td>
                      <td className="py-3 px-2">
                        <p className="font-medium text-slate-900">{item.child_name}</p>
                        <p className="text-xs text-slate-500">{item.parent_name}</p>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="font-medium text-slate-900">
                          {formatCurrency(item.total_amount_cents)} PLN
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className={cn(
                          "font-medium",
                          item.days_overdue > 30 ? "text-red-600" :
                          item.days_overdue > 14 ? "text-orange-500" : "text-slate-600"
                        )}>
                          {item.days_overdue} dni
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        {item.has_open_dispute ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Dyskusja
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Nieopłacony
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm text-slate-500">
                        {item.reminder_count > 0 ? (
                          <span title={`Wysłano ${item.reminder_count}x, ostatnio ${formatReminderTime(item.reminder_sent_at)}`}>
                            {formatReminderTime(item.reminder_sent_at)}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRowClick(item.receipt_id)}
                          className="h-8 w-8 p-0"
                        >
                          <ExternalLink className="w-4 h-4 text-slate-400" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wyślij przypomnienie o płatności</DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz wysłać przypomnienie do {selectedIds.size} rodziców?
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Liczba rachunków:</span>
                <span className="font-medium">{selectedIds.size}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Łączna kwota zaległości:</span>
                <span className="font-medium text-red-600">{formatCurrency(totalSelectedAmount)} PLN</span>
              </div>
            </div>

            <p className="text-sm text-slate-500 mt-4">
              Rodzice otrzymają powiadomienie w aplikacji oraz wiadomość email.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
              disabled={sendReminder.isPending}
            >
              Anuluj
            </Button>
            <Button
              onClick={handleSendReminders}
              disabled={sendReminder.isPending}
            >
              {sendReminder.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Wysyłanie...
                </>
              ) : (
                'Wyślij'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
